import { iconButtonClasses } from '@mui/material'
import { Theme } from '@mui/material/styles'

import { icons, layout } from '../tokens.generated'
import { FOCUS_VISIBLE, focusRingShadow } from './Button'

// ----------------------------------------------------------------------
// Icon-only controls are square, on the control radius, and sit on a sunken
// fill when hovered (handover 4.4). The box is a minimum rather than a fixed
// size so a button that still wraps a larger child keeps working until that
// child is replaced; an icon on its own resolves to exactly the square.
//
// On a touch viewport every control has to reach the 44 hit target, so a
// pseudo element pads the hit area out while the visible box stays put.
// ----------------------------------------------------------------------

/** The palette colours an IconButton can be asked for by name. */
const NAMED_COLORS = [
  'primary',
  'secondary',
  'info',
  'success',
  'warning',
  'error',
  'tesla',
] as const

/** The touch hit target every control has to reach. */
const TOUCH_TARGET = layout.controlHeightLarge

const capitalize = (value: string) =>
  value.charAt(0).toUpperCase() + value.slice(1)

/**
 * One square size. `box` is the visible control, `glyph` the icon ramp step it
 * carries. The edge margins pull the glyph flush with the container's content
 * edge, which is what `edge="start"` and `edge="end"` promise, so they are the
 * glyph's inset inside the box rather than MUI's fixed numbers.
 */
const square = (theme: Theme, box: number, glyph: number) => ({
  'minWidth': box,
  'minHeight': box,
  'fontSize': theme.typography.pxToRem(glyph),
  // The box stays where it is; the hit area reaches the touch target.
  '@media (pointer: coarse)': {
    '&::after': {
      content: '""',
      position: 'absolute' as const,
      inset: -((TOUCH_TARGET - box) / 2),
    },
  },
})

const glyphInset = (box: number, glyph: number) => (box - glyph) / 2

export default function IconButton(theme: Theme) {
  const sizes = {
    small: { box: layout.controlHeightSmall, glyph: icons.sizes.dense },
    medium: { box: layout.iconButtonSize, glyph: icons.sizes.control },
    large: { box: layout.iconButtonSizeLarge, glyph: icons.sizes.toolbar },
  }

  // A named colour keeps its own glyph colour at rest and on hover; only the
  // fill under it changes. Without this the default treatment below, which is
  // applied last, would turn every coloured icon button grey.
  const namedColors = Object.fromEntries(
    NAMED_COLORS.map((name) => [
      `color${capitalize(name)}`,
      {
        'color': theme.palette[name].main,
        '&:hover': { color: theme.palette[name].main },
      },
    ]),
  )

  return {
    MuiIconButton: {
      styleOverrides: {
        root: {
          ...square(theme, sizes.medium.box, sizes.medium.glyph),
          'padding': 0,
          'borderRadius': theme.radii.control,
          'color': theme.palette.text.secondary,
          'transition': theme.motion.state.transition(
            'background-color',
            'color',
          ),
          // Hover lifts the glyph one step and puts the sunken surface under
          // it, so the control reads as one object. Colour only, no movement.
          '&:hover': {
            'backgroundColor': theme.hover.surface,
            'color': theme.hover.ink,
            // A tap is not a hover; the fill would otherwise stick after it.
            '@media (hover: none)': {
              backgroundColor: 'transparent',
            },
          },
          [FOCUS_VISIBLE]: {
            outline: 'none',
            boxShadow: focusRingShadow(theme),
          },
        },
        ...namedColors,
        colorInherit: {
          'color': 'inherit',
          '&:hover': { color: 'inherit' },
        },
        sizeSmall: square(theme, sizes.small.box, sizes.small.glyph),
        sizeLarge: square(theme, sizes.large.box, sizes.large.glyph),
        edgeStart: {
          marginLeft: -glyphInset(sizes.medium.box, sizes.medium.glyph),
          [`&.${iconButtonClasses.sizeSmall}`]: {
            marginLeft: -glyphInset(sizes.small.box, sizes.small.glyph),
          },
          [`&.${iconButtonClasses.sizeLarge}`]: {
            marginLeft: -glyphInset(sizes.large.box, sizes.large.glyph),
          },
        },
        edgeEnd: {
          marginRight: -glyphInset(sizes.medium.box, sizes.medium.glyph),
          [`&.${iconButtonClasses.sizeSmall}`]: {
            marginRight: -glyphInset(sizes.small.box, sizes.small.glyph),
          },
          [`&.${iconButtonClasses.sizeLarge}`]: {
            marginRight: -glyphInset(sizes.large.box, sizes.large.glyph),
          },
        },
      },
    },
  }
}
